import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListCardSkeleton } from '../Common/PageSkeleton';
import userAPI from '../../services/userAPI';
import { useTranslate } from '../../Hook/useTranslate';
import LocationPickerModal from '../Common/LocationPickerModal';
import FormBanner from '../Common/FormBanner';
import { Plus, Trash2, Home, Briefcase, Map } from 'lucide-react';
import './Profile.css';

const Addresses = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formBanner, setFormBanner] = useState({ type: '', text: '' });
  const navigate = useNavigate();
  const t = useTranslate();

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const data = await userAPI.getAddresses();
      setAddresses(data.addresses || []);
    } catch (err) {
      console.error('Failed to fetch addresses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddress = async (data) => {
    setFormBanner({ type: '', text: '' });
    try {
      const newAddress = {
        addressLine: data.address,
        houseNo: data.houseNo || '',
        streetArea: data.streetArea || data.rawAddress || '',
        landmark: data.landmark || '',
        city: data.city || data.addressData?.city || data.address?.split(',').slice(-3, -2)[0]?.trim() || '',
        state: data.state || data.addressData?.state || '',
        stateCode: data.stateCode || '',
        country: data.country || data.addressData?.country || '',
        countryCode: data.countryCode || data.addressData?.countryCode || '',
        zipCode: data.postalCode || data.zipCode || data.addressData?.postalCode || data.addressData?.zipCode || data.addressData?.pincode || '',
        postalCode: data.postalCode || data.zipCode || data.addressData?.postalCode || data.addressData?.zipCode || data.addressData?.pincode || '',
        location: {
          type: "Point",
          coordinates: [data.lng, data.lat]
        },
        label: data.label || 'Other'
      };
      
      await userAPI.addAddress(newAddress);
      fetchAddresses();
      setIsModalOpen(false);
    } catch (err) {
      setFormBanner({ type: 'error', text: 'Failed to add address' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      try {
        await userAPI.deleteAddress(id);
        setAddresses(addresses.filter(a => a._id !== id));
      } catch (err) {
        setFormBanner({ type: 'error', text: 'Failed to delete address' });
      }
    }
  };

  const handleSetDefault = async (addr) => {
    try {
      await userAPI.updateAddress(addr._id, { isDefault: true });
      
      // Update localStorage so Home page picks up the new location
      const shortAddress = addr.addressLine.split(',')[0] + ', ' + (addr.city || "");
      localStorage.setItem('userLocation', shortAddress);
      localStorage.setItem('userFullAddress', JSON.stringify({
        ...addr,
        formattedAddress: addr.addressLine,
        coordinates: addr.location.coordinates
      }));

      // Navigate to home page
      navigate('/home');
    } catch (err) {
      setFormBanner({ type: 'error', text: 'Failed to select address' });
    }
  };

  const getIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'home': return <Home size={18} />;
      case 'work': return <Briefcase size={18} />;
      default: return <Map size={18} />;
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-header-bar">
        <button onClick={() => navigate(-1)} className="back-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <h1>Saved Addresses</h1>
        <button className="add-addr-btn-header" onClick={() => setIsModalOpen(true)}>
          <Plus size={20} />
        </button>
      </div>

      <div className="addresses-container">
        <FormBanner
          message={formBanner.text}
          type={formBanner.type || 'error'}
          onDismiss={() => setFormBanner({ type: '', text: '' })}
        />
        {loading ? (
          <ListCardSkeleton count={3} />
        ) : addresses.length === 0 ? (

          <div className="addr-empty">
            <div className="empty-icon">📍</div>
            <h3>No Addresses Found</h3>
            <p>Add your home, work or other addresses for faster checkout</p>
            <button className="add-first-addr-btn" onClick={() => setIsModalOpen(true)}>
              Add New Address
            </button>
          </div>
        ) : (
          <div className="address-grid-premium">
            {addresses.map((addr) => (
              <div 
                key={addr._id} 
                className={`address-card-v3 ${addr.isDefault ? 'is-default' : ''}`}
                onClick={() => handleSetDefault(addr)}
                style={{ cursor: 'pointer' }}
              >
                <div className="addr-icon-box">
                  {getIcon(addr.type || addr.label)}
                </div>
                <div className="addr-details-box">
                  <div className="addr-title-row">
                    <h3>{addr.label || addr.type || 'Other'}</h3>
                    {addr.isDefault && <span className="default-tag">Default</span>}
                  </div>
                  <p className="addr-line">{addr.addressLine}</p>
                  <p className="addr-city">{addr.city} {addr.postalCode || addr.zipCode}</p>
                </div>
                <button 
                  className="delete-addr-btn" 
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent setting as default when deleting
                    handleDelete(addr._id);
                  }}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <LocationPickerModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleAddAddress}
      />
    </div>
  );
};

export default Addresses;
